import { useState, useEffect } from 'react';
import { doc, onSnapshot, runTransaction, increment, arrayUnion, arrayRemove, Timestamp, getDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHandsClapping } from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';

export default function ClapButton({ contentId, contentType, size = 'md', showCount = true }) {
  const { user } = useAuth();
  const [claps, setClaps] = useState(0);
  const [isClapped, setIsClapped] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isAnimating, setIsAnimating] = useState(false);

  // Load clap data from Firestore - use separate 'claps' collection for all content
  useEffect(() => {
    if (!contentId) {
      setLoading(false);
      return;
    }

    // Use claps collection for all content (works for both Firestore and static content)
    const clapsRef = doc(db, 'claps', contentId);
    
    const unsubscribe = onSnapshot(
      clapsRef,
      (doc) => {
        if (doc.exists()) {
          const data = doc.data();
          setClaps(data.count || 0);
          setIsClapped(user && (data.clappedBy || []).includes(user.uid));
        } else {
          // No claps yet
          setClaps(0);
          setIsClapped(false);
        }
        setLoading(false);
      },
      (error) => {
        console.error('Error loading claps:', error);
        setClaps(0);
        setIsClapped(false);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [contentId, user?.uid]);

  const handleClap = async () => {
    if (!user) {
      toast.error('Please sign in to clap');
      return;
    }

    if (!contentId) {
      toast.error('Content not found');
      return;
    }

    // Optimistic update
    const wasClapped = isClapped;
    setIsClapped(!wasClapped);
    setClaps(prev => wasClapped ? Math.max(0, prev - 1) : prev + 1);
    setIsAnimating(true);
    setTimeout(() => setIsAnimating(false), 600);

    try {
      // Use claps collection for all content
      const clapsRef = doc(db, 'claps', contentId);
      
      await runTransaction(db, async (transaction) => {
        const clapsDoc = await transaction.get(clapsRef);
        
        const currentClappedBy = clapsDoc.exists() ? (clapsDoc.data().clappedBy || []) : [];
        const isCurrentlyClapped = currentClappedBy.includes(user.uid);

        if (isCurrentlyClapped) {
          // Unclap
          if (clapsDoc.exists()) {
            const currentCount = clapsDoc.data().count || 0;
            if (currentCount > 0) {
              transaction.update(clapsRef, {
                count: increment(-1),
                clappedBy: arrayRemove(user.uid),
                updatedAt: Timestamp.now()
              });
            }
          }
        } else {
          // Clap
          if (clapsDoc.exists()) {
            transaction.update(clapsRef, {
              count: increment(1),
              clappedBy: arrayUnion(user.uid),
              updatedAt: Timestamp.now()
            });
          } else {
            // Create new clap document
            transaction.set(clapsRef, {
              contentId: contentId,
              contentType: contentType,
              count: 1,
              clappedBy: [user.uid],
              createdAt: Timestamp.now(),
              updatedAt: Timestamp.now()
            });
          }
        }
      });
    } catch (error) {
      // Revert optimistic update
      setIsClapped(wasClapped);
      setClaps(prev => wasClapped ? prev + 1 : Math.max(0, prev - 1));
      
      console.error('Error toggling clap:', error);
      toast.error('Failed to update clap');
    }
  };

  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-base',
    lg: 'px-6 py-3 text-lg'
  };

  const iconSizes = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6'
  };

  if (loading) {
    return (
      <button
        disabled
        className={`flex items-center gap-2 ${sizeClasses[size]} rounded-lg bg-gray-100 text-text-secondary cursor-not-allowed`}
      >
        <FontAwesomeIcon icon={faHandsClapping} className={iconSizes[size]} />
        {showCount && <span>...</span>}
      </button>
    );
  }

  return (
    <button
      onClick={handleClap}
      disabled={!contentId}
      className={`flex items-center gap-2 ${sizeClasses[size]} rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
        isClapped
          ? 'bg-primary text-white shadow-md'
          : 'bg-white text-text-secondary hover:bg-gray-100 border border-border'
      } ${isAnimating ? 'scale-110' : ''} disabled:opacity-50 disabled:cursor-not-allowed`}
      aria-label={isClapped ? 'Unclap this content' : 'Clap for this content'}
      aria-pressed={isClapped}
    >
      <FontAwesomeIcon 
        icon={faHandsClapping} 
        className={`${iconSizes[size]} ${isAnimating ? 'animate-bounce' : ''}`}
      />
      {showCount && (
        <span className="font-semibold">
          {claps}
        </span>
      )}
    </button>
  );
}

